import { DocumentCard } from '@dealstack/frontend/shared/components/DocumentCard/DocumentCard';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { Divider } from '@dealstack/frontend/shared/components/fundament/Divider/Divider';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import {
	Button,
	ButtonProps,
	createPolymorphicComponent,
	em,
	Grid,
	Group,
	GroupProps,
	NumberInput,
	NumberInputProps,
	TextInput,
	TextInputProps,
	GridColProps,
} from '@mantine/core';
import styled, { css } from 'styled-components';
import { asPlainComponent } from '@dealstack/frontend/shared/utils/asPlainComponent';

export const GuidanceNoteWrapper = styled.div``;
export const GuidanceNoteDocument = styled(DocumentCard)`
	max-width: 10rem;
`;
export const CounterpartyInputWrapper = styled.div`
	display: flex;
	gap: 0.5rem;
	form {
		display: contents;
	}
`;
const _StyledIconButton = styled(Button)<ButtonProps>`
	& .mantine-Button-section {
		width: 0.5rem;
		height: 0.5rem;
	}
	width: 2rem;
	&& {
		height: 2rem;
		padding: 10px;
	}
`;
export const StyledIconButton = asPlainComponent<
	ButtonProps & { type?: 'button' | 'submit' | 'reset' }
>(createPolymorphicComponent<'button', ButtonProps>(_StyledIconButton));
const _StyledNumberInputGroup = styled(Group)<GroupProps>`
	border: 1px solid ${({ theme }) => theme.v2.color.neutral[300]};
	padding: 0 0.125rem;
	border-radius: 0.25rem;
	&:focus-within {
		border-color: ${defaultTheme.v2.color.primary[800]};
	}
	&:hover {
		border: solid 1px ${defaultTheme.v2.color.neutral[700]};
	}
	gap: 0;
	flex-wrap: nowrap;
	// Fixed, not max: the padding no longer props the pill up to the name field's height.
	height: 2rem;
	${Icon} {
		color: ${defaultTheme.v2.color.black[500]};
	}
`;
export const StyledNumberInputGroup = asPlainComponent<GroupProps>(
	createPolymorphicComponent<'div', GroupProps>(_StyledNumberInputGroup)
);
const _StyledTextInput = styled(TextInput)<TextInputProps>`
	display: flex;
	flex-direction: column;

	input {
		margin: 0;
		min-height: 2rem;
		max-height: 2rem;
		min-width: 13.25rem;
		&:focus {
			border-color: ${defaultTheme.v2.color.primary[800]};
		}
		&:hover {
			border: solid 1px ${defaultTheme.v2.color.neutral[700]};
		}
	}

	& .mantine-Text-root {
		color: ${defaultTheme.v2.color.neutral[1100]};
	}
`;
export const StyledTextInput = asPlainComponent<TextInputProps>(
	createPolymorphicComponent<'input', TextInputProps>(_StyledTextInput)
);
export const NumberInputWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	min-width: 6rem;
`;
const _StyledNumberInput = styled(NumberInput)<NumberInputProps>`
	flex: 1 1 auto;
	min-width: 0;

	// The base Input theme pads section-less inputs by 8px and hides their overflow,
	// which left this ~22px-wide input a 4px content box and clipped the digit.
	// The repeated & is needed to outrank that theme rule's :not(...) selectors.
	&&&& input {
		margin: 0;
		padding: 0;
		height: 100%;
		min-height: 0;
		text-align: center;
		font-size: 0.875rem;
		overflow: visible;
		text-overflow: clip;
		// The pill draws the surface; a background here reads as a blob inside it.
		background: transparent;
	}
`;
export const StyledNumberInput = asPlainComponent<NumberInputProps>(
	createPolymorphicComponent<'input', NumberInputProps>(_StyledNumberInput)
);
export const ScrollableSection = styled.div`
	display: flex;
	gap: 0.25rem;
`;
export const CounterpartiesChipsWrapper = styled.div<{ $showChips: boolean; $disabled: boolean }>`
	${({ $disabled }) =>
		$disabled &&
		css`
			opacity: 0.32;
			pointer-events: none;
		`};
	${({ $showChips }) =>
		$showChips &&
		css`
			padding-bottom: 0.5rem;
		`};
	overflow: auto;
	::-webkit-scrollbar {
		width: ${em(80)};
		height: ${em(4)};
	}

	::-webkit-scrollbar-thumb {
		background: ${defaultTheme.v2.color.neutral[400]};
		border-radius: 0.5rem;
	}

	::-webkit-scrollbar-thumb:hover {
		background-color: ${defaultTheme.v2.color.neutral[600]};
	}
`;
export const ChipChildrenWrapper = styled.div`
	display: flex;
	gap: 0.25rem;
	align-items: center;
`;
export const SignatureTypeRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;
export const SignatureTypeSwitchWrapper = styled.div`
	display: flex;
	gap: 0.25rem;
	align-items: center;
`;
export const StyledDivider = styled(Divider)`
	margin: 0.5rem 0;
`;
export const StyledGridColumn = styled(Grid.Col)<GridColProps>`
	padding-top: 0.5rem;
`;
export const TemplateTypesHorizontalList = styled.div`
	display: flex;
	align-items: center;
	overflow: auto;
	padding-bottom: 0.5rem;

	&::-webkit-scrollbar {
		height: 0.25rem;
	}

	&::-webkit-scrollbar-track {
		background-color: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background-color: ${defaultTheme.v2.color.neutral[400]};
		border-radius: 0.5rem;
	}

	&::-webkit-scrollbar-thumb:hover {
		background-color: ${defaultTheme.v2.color.neutral[600]};
	}
`;
export const RadioGroupWrapper = styled.div`
	max-width: 10rem;
	display: flex;
	gap: 0.5rem;
	flex-direction: row;
`;
export const LeftPanel = styled.div`
	border-radius: 0.25rem;
	border: solid 1px ${defaultTheme.v2.color.neutral[300]};
	background-color: ${defaultTheme.v2.color.white[500]};
	min-height: 16rem;
	padding: 0.5rem;
	> div:not(:first-child) {
		margin-top: 0.5rem;
	}
`;
export const RightPanel = styled.div`
	border-radius: 0.25rem;
	border: solid 1px ${defaultTheme.v2.color.neutral[300]};
	background-color: ${defaultTheme.v2.color.white[500]};
	min-height: 13.75rem;
	padding: 0.5rem;
	> div:not(:first-child) {
		margin-top: 0.5rem;
	}
	${ButtonV2} {
		margin-top: 0.5rem;
	}
`;
export const InternalGroupWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	padding: 0.5rem;
	border-radius: 0.25rem;
	border: solid 1px ${defaultTheme.v2.color.neutral[300]};
`;
